// src/screens/WealthTrajectory.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Phase 3 — 4-tab What-If scenario suite.
// Tab 1: Overview (existing stacked bar)
// Tab 2: Mortgage Scenarios (standard / +SO / +SO + Annual Lump Sum)
// Tab 3: Investment What-Ifs (ISA scenarios / Pension flat vs stepped / ISA vs mortgage)
// Tab 4: Full Integrated Plan (year-by-year table + milestones)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { ClientData } from '../types/ClientData';
import { formatCurrency, formatCurrencyK } from '../utils/format';
import { useCommaInput } from '../utils/useCommaInput';
import { effectiveCTRate } from '../taxConfig';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

const fmtK = formatCurrencyK;

// ─── Growth assumptions (from Assumptions tool, with fallbacks) ───────────────
function getGrowth(data: { assumptionPensionGrowth?: number; assumptionIsaGrowth?: number; assumptionCashRate?: number; assumptionPropertyGrowth?: number }) {
  return {
    pension: data.assumptionPensionGrowth || 0.08,
    isa: data.assumptionIsaGrowth || 0.08,
    cash: data.assumptionCashRate || 0.01,
    property: data.assumptionPropertyGrowth || 0.01,
  };
}

type TabId = 'overview' | 'mortgage' | 'investment' | 'fullPlan';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview', icon: '📊' },
  { id: 'mortgage', label: 'Mortgage', icon: '🏠' },
  { id: 'investment', label: 'Investments', icon: '📈' },
  { id: 'fullPlan', label: 'Full Plan', icon: '🗺' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1: OVERVIEW — existing stacked bar chart
// ═══════════════════════════════════════════════════════════════════════════════

interface YearProjection {
  year: number;
  age: number;
  pension: number;
  isa: number;
  cash: number;
  property: number;
  total: number;
}

function projectWealth(data: ClientData): YearProjection[] {
  const G = getGrowth(data);
  const years: YearProjection[] = [];
  const currentYear = new Date().getFullYear();
  let pension = data.pensionBalance || 0;
  let isa = data.investmentsBalance || 0;
  let cash = (data.efAccount1Balance || 0) + (data.efAccount2Balance || 0) + (data.otherSavingsBalance || 0);

  const pensionLever = data.leversApplied.find(l => l.leverKey === 'lever5_pension_personal' && l.applied);
  const isaLever = data.leversApplied.find(l => l.leverKey === 'lever4_isa' && l.applied);
  const efLever = data.leversApplied.find(l => l.leverKey === 'lever2_ef' && l.applied);
  const mortgageOPLever = data.leversApplied.find(l => l.leverKey === 'lever6_mortgage_op' && l.applied);

  const pensionMonthly = (data.pensionCompanyFunded || 0) + (pensionLever?.monthlyAmount || data.pensionMonthly || 0);
  const isaMonthly = isaLever?.monthlyAmount || data.isaContribution || 0;
  const cashMonthly = efLever?.monthlyAmount || 0;
  const mortgageOPMonthly = mortgageOPLever?.monthlyAmount || data.mortgageOP || 0;

  for (let i = 0; i <= 10; i++) {
    const propGross = (data.propertyValue || 0) * Math.pow(1 + G.property, i) +
                      (data.rentalPropertyValue || 0) * Math.pow(1 + G.property, i);
    const mortRemaining = Math.max(0, (data.mortgageBalance || 0) - (data.mortgagePayment || 0) * 12 * i - mortgageOPMonthly * 12 * i);
    const rentalMortRemaining = Math.max(0, (data.rentalMortgageBalance || 0) - (data.rentalMortgageType === 'repayment' ? 500 * 12 * i : 0));
    const property = propGross - mortRemaining - rentalMortRemaining;

    years.push({
      year: currentYear + i,
      age: data.clientAge + i,
      pension: Math.round(pension),
      isa: Math.round(isa),
      cash: Math.round(cash),
      property: Math.round(property),
      total: Math.round(pension + isa + cash + property),
    });

    pension = pension * (1 + G.pension) + pensionMonthly * 12;
    isa = isa * (1 + G.isa) + isaMonthly * 12;
    cash = cash * (1 + G.cash) + cashMonthly * 12;
  }
  return years;
}

function OverviewTab({ data }: { data: ClientData }) {
  const projections = useMemo(() => projectWealth(data), [data]);

  const chartData = useMemo(() => ({
    labels: projections.map(p => `${p.year}`),
    datasets: [
      { label: 'Pension', data: projections.map(p => p.pension), backgroundColor: '#001F8C', borderRadius: 2 },
      { label: 'ISA', data: projections.map(p => p.isa), backgroundColor: '#64DAD2', borderRadius: 2 },
      { label: 'Cash', data: projections.map(p => p.cash), backgroundColor: '#60A5FA', borderRadius: 2 },
      { label: 'Property (net)', data: projections.map(p => Math.max(0, p.property)), backgroundColor: '#34D399', borderRadius: 2 },
    ],
  }), [projections]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const, labels: { usePointStyle: true, pointStyle: 'circle' as const, padding: 16, font: { size: 12 } } },
      title: { display: false },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${fmt(ctx.raw)}` } },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { font: { size: 10 }, callback: function(this: any, _v: any, i: number) { const p = projections[i]; return [`${p.year}`, `Age ${p.age}`]; } } },
      y: { stacked: true, grid: { color: '#f3f4f6' }, ticks: { callback: (v: any) => fmtK(v), font: { size: 10 } } },
    },
  }), [projections]);

  const first = projections[0];
  const last = projections[projections.length - 1];
  const growth = last.total - first.total;
  const growthPct = first.total > 0 ? ((last.total / first.total - 1) * 100).toFixed(0) : '—';

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Today" value={fmt(first.total)} />
        <SummaryCard label="Year 10" value={fmt(last.total)} />
        <SummaryCard label="Growth" value={`+${fmt(growth)}`} colour="text-green-600" />
        <SummaryCard label="Growth %" value={`+${growthPct}%`} colour="text-green-600" />
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" style={{ height: 420 }}>
        <Bar data={chartData} options={options} />
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2: MORTGAGE SCENARIOS
// ═══════════════════════════════════════════════════════════════════════════════

interface MortgageYear {
  year: number;
  age: number;
  balanceStandard: number;
  balanceWithSO: number;
  balanceWithSOLump: number;
  interestStandard: number;
  interestWithSO: number;
  interestWithSOLump: number;
}

function projectMortgage(data: ClientData, annualLumpAmt: number): { years: MortgageYear[]; freeAgeStandard: number | null; freeAgeSO: number | null; freeAgeSOLump: number | null; totalIntStd: number; totalIntSO: number; totalIntSOLump: number } {
  const bal = data.mortgageBalance || 0;
  const rate = (data.mortgageRate || 0) / 100;
  const payment = data.mortgagePayment || 0;
  const op = data.mortgageOP || 0;
  const isIO = data.mortgageIsInterestOnly;
  const currentYear = new Date().getFullYear();
  const term = data.mortgageTermYears || 25;

  let balStd = bal, balSO = bal, balSOLump = bal;
  let totalIntStd = 0, totalIntSO = 0, totalIntSOLump = 0;
  let freeAgeStd: number | null = null, freeAgeSO: number | null = null, freeAgeSOLump: number | null = null;

  const years: MortgageYear[] = [];

  for (let i = 0; i <= Math.min(term + 5, 30); i++) {
    const yr = currentYear + i;
    const age = data.clientAge + i;

    const intStd = balStd * rate;
    const intSO = balSO * rate;
    const intSOLump = balSOLump * rate;
    totalIntStd += intStd;
    totalIntSO += intSO;
    totalIntSOLump += intSOLump;

    years.push({
      year: yr, age,
      balanceStandard: Math.round(Math.max(0, balStd)),
      balanceWithSO: Math.round(Math.max(0, balSO)),
      balanceWithSOLump: Math.round(Math.max(0, balSOLump)),
      interestStandard: Math.round(intStd),
      interestWithSO: Math.round(intSO),
      interestWithSOLump: Math.round(intSOLump),
    });

    if (balStd <= 0 && freeAgeStd === null) freeAgeStd = age;
    if (balSO <= 0 && freeAgeSO === null) freeAgeSO = age;
    if (balSOLump <= 0 && freeAgeSOLump === null) freeAgeSOLump = age;

    // Standard: just scheduled payments
    const principalStd = isIO ? 0 : Math.max(0, payment * 12 - intStd);
    balStd = Math.max(0, balStd - principalStd);

    // With SO: scheduled + overpayment
    const principalSO = isIO ? op * 12 : Math.max(0, (payment + op) * 12 - intSO);
    balSO = Math.max(0, balSO - principalSO);

    // With SO + Annual Lump Sum
    const principalSOLump = isIO ? op * 12 + annualLumpAmt : Math.max(0, (payment + op) * 12 - intSOLump) + annualLumpAmt;
    balSOLump = Math.max(0, balSOLump - principalSOLump);

    if (balStd <= 0 && balSO <= 0 && balSOLump <= 0) break;
  }

  return { years, freeAgeStandard: freeAgeStd, freeAgeSO: freeAgeSO, freeAgeSOLump: freeAgeSOLump, totalIntStd: Math.round(totalIntStd), totalIntSO: Math.round(totalIntSO), totalIntSOLump: Math.round(totalIntSOLump) };
}

function MortgageTab({ data, onUpdate }: { data: ClientData; onUpdate: (u: Partial<ClientData>) => void }) {
  // Annual lump sum: use mortgageAnnualLumpSum field
  const [lumpSumOverride, setLumpSumOverride] = useState<number>(data.mortgageAnnualLumpSum || 0);
  const lumpSumInput = useCommaInput(lumpSumOverride, (v) => {
    setLumpSumOverride(v);
    onUpdate({ mortgageAnnualLumpSum: v });
  });
  const m = useMemo(() => projectMortgage(data, lumpSumOverride), [data, lumpSumOverride]);

  const chartData = useMemo(() => ({
    labels: m.years.map(y => `${y.year}`),
    datasets: [
      { label: 'Standard only', data: m.years.map(y => y.balanceStandard), borderColor: '#94A3B8', backgroundColor: '#94A3B820', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 1 },
      { label: '+ SO overpayment', data: m.years.map(y => y.balanceWithSO), borderColor: '#64DAD2', backgroundColor: '#64DAD220', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 1 },
      { label: '+ SO + Annual Lump', data: m.years.map(y => y.balanceWithSOLump), borderColor: '#001F8C', backgroundColor: '#001F8C20', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 1 },
    ],
  }), [m]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const, labels: { usePointStyle: true, pointStyle: 'circle' as const, padding: 16, font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${fmt(ctx.raw)}` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 15, callback: function(this: any, _v: any, i: number) { const y = m.years[i]; return i % 2 === 0 ? [`${y.year}`, `Age ${y.age}`] : ''; } } },
      y: { grid: { color: '#f3f4f6' }, ticks: { callback: (v: any) => fmtK(v), font: { size: 10 } }, beginAtZero: true },
    },
  }), [m]);

  if (!data.mortgageBalance) {
    return <div className="text-center text-gray-400 py-12">No mortgage data entered.</div>;
  }

  const intSaved = m.totalIntStd - m.totalIntSOLump;

  return (
    <>
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Mortgage balance" value={fmt(data.mortgageBalance)} />
        <SummaryCard label="Rate" value={`${data.mortgageRate}%`} />
        <SummaryCard label="Monthly payment" value={fmt(data.mortgagePayment)} />
        <SummaryCard label="Overpayment SO" value={fmt(data.mortgageOP || 0)} colour={data.mortgageOP ? 'text-green-600' : 'text-gray-400'} />
      </div>

      {/* Annual lump sum input */}
      <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
        <label className="text-xs text-gray-500 font-medium uppercase tracking-wide">Annual Lump Sum for Scenario C</label>
        <p className="text-xs text-gray-400 mb-2">
          Enter any planned annual lump sum payment — bonus, inheritance, capital release, etc.
        </p>
        <div className="flex items-center bg-white border border-gray-200 rounded-xl px-4 py-2 w-48 focus-within:ring-2 focus-within:ring-ma-navy/20">
          <span className="text-gray-400 text-sm mr-2">£</span>
          <input
            {...lumpSumInput}
            placeholder="0"
            className="flex-1 bg-transparent text-lg font-bold text-ma-navy outline-none"
          />
          <span className="text-gray-400 text-xs ml-2">/year</span>
        </div>
      </div>

      {/* Mortgage-free ages */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-xs text-gray-400 uppercase">Standard</div>
          <div className="text-2xl font-bold text-gray-500">{m.freeAgeStandard ? `Age ${m.freeAgeStandard}` : '—'}</div>
          <div className="text-xs text-gray-400 mt-1">Interest: {fmt(m.totalIntStd)}</div>
        </div>
        <div className="bg-ma-teal/10 rounded-xl border border-ma-teal/30 p-4 text-center">
          <div className="text-xs text-gray-500 uppercase">+ Overpayment</div>
          <div className="text-2xl font-bold text-ma-navy">{m.freeAgeSO ? `Age ${m.freeAgeSO}` : '—'}</div>
          <div className="text-xs text-gray-400 mt-1">Interest: {fmt(m.totalIntSO)}</div>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
          <div className="text-xs text-gray-500 uppercase">+ SO + Annual Lump</div>
          <div className="text-2xl font-bold text-ma-navy">{m.freeAgeSOLump ? `Age ${m.freeAgeSOLump}` : '—'}</div>
          <div className="text-xs text-green-600 mt-1 font-medium">Save {fmt(intSaved)} interest</div>
        </div>
      </div>

      {data.mortgageIsInterestOnly && (
        <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-xs text-amber-700">⚠ Interest-only mortgage — balance does not reduce with standard payments. Overpayments shown reduce the capital directly.</p>
        </div>
      )}

      {/* Chart */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" style={{ height: 380 }}>
        <Line data={chartData} options={chartOptions} />
      </div>

      {/* Year-by-year table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-3 py-2 text-left text-gray-400 font-medium">Year</th>
              <th className="px-3 py-2 text-left text-gray-400 font-medium">Age</th>
              <th className="px-3 py-2 text-right text-gray-400 font-medium">Standard</th>
              <th className="px-3 py-2 text-right text-gray-400 font-medium">+ SO</th>
              <th className="px-3 py-2 text-right text-gray-400 font-medium">+ SO + Lump</th>
            </tr>
          </thead>
          <tbody>
            {m.years.map((y, i) => (
              <tr key={y.year} className={`border-b border-gray-50 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="px-3 py-1.5 font-medium text-gray-600">{y.year}</td>
                <td className="px-3 py-1.5 text-gray-500">{y.age}</td>
                <td className="px-3 py-1.5 text-right font-medium text-gray-600">{y.balanceStandard > 0 ? fmt(y.balanceStandard) : <span className="text-green-600 font-bold">PAID OFF</span>}</td>
                <td className="px-3 py-1.5 text-right font-medium text-ma-navy">{y.balanceWithSO > 0 ? fmt(y.balanceWithSO) : <span className="text-green-600 font-bold">PAID OFF</span>}</td>
                <td className="px-3 py-1.5 text-right font-medium text-ma-navy">{y.balanceWithSOLump > 0 ? fmt(y.balanceWithSOLump) : <span className="text-green-600 font-bold">PAID OFF</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3: INVESTMENT WHAT-IFS
// ═══════════════════════════════════════════════════════════════════════════════

function InvestmentTab({ data }: { data: ClientData }) {
  const G = getGrowth(data);
  const yearsToFreedom = Math.max(1, (data.targetFreedomAge || 60) - (data.clientAge || 40));
  const currentYear = new Date().getFullYear();

  // ─── ISA: 3 scenarios (low/mid/high) ──────────────────────────────────────
  const isaScenarios = useMemo(() => {
    const current = data.isaContribution || 0;
    const low = Math.max(current, 200);
    const mid = Math.max(current, 500);
    const high = Math.max(current, 1000);

    function projectISA(monthly: number) {
      let bal = data.investmentsBalance || 0;
      const years: number[] = [];
      for (let i = 0; i <= yearsToFreedom; i++) {
        years.push(Math.round(bal));
        bal = bal * (1 + G.isa) + monthly * 12;
      }
      return years;
    }

    return {
      labels: Array.from({ length: yearsToFreedom + 1 }, (_, i) => `${currentYear + i}`),
      low: { monthly: low, projections: projectISA(low) },
      mid: { monthly: mid, projections: projectISA(mid) },
      high: { monthly: high, projections: projectISA(high) },
    };
  }, [data, yearsToFreedom, currentYear]);

  const isaChartData = useMemo(() => ({
    labels: isaScenarios.labels,
    datasets: [
      { label: `£${isaScenarios.low.monthly}/mo`, data: isaScenarios.low.projections, borderColor: '#94A3B8', backgroundColor: '#94A3B820', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 1 },
      { label: `£${isaScenarios.mid.monthly}/mo`, data: isaScenarios.mid.projections, borderColor: '#64DAD2', backgroundColor: '#64DAD220', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 1 },
      { label: `£${isaScenarios.high.monthly}/mo`, data: isaScenarios.high.projections, borderColor: '#001F8C', backgroundColor: '#001F8C20', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 1 },
    ],
  }), [isaScenarios]);

  // ─── Pension: flat vs stepped ─────────────────────────────────────────────
  const pensionScenarios = useMemo(() => {
    const flatMonthly = (data.pensionCompanyFunded || 0) + (data.pensionMonthly || 0);
    const stepped = data.pensionSteppedSchedule || [];

    function projectPension(useSteps: boolean) {
      let bal = data.pensionBalance || 0;
      const years: number[] = [];
      for (let i = 0; i <= yearsToFreedom; i++) {
        years.push(Math.round(bal));
        const yr = currentYear + i;
        let monthly = flatMonthly;
        if (useSteps && stepped.length > 0) {
          const activeStep = [...stepped].reverse().find(s => s.fromYear <= yr);
          if (activeStep) monthly = activeStep.monthlyAmount + (data.pensionMonthly || 0);
        }
        bal = bal * (1 + G.pension) + monthly * 12;
        // CT relief on the stepped contribution — uses the client's actual
        // effective CT rate rather than a flat 19% approximation.
        if (useSteps) bal += monthly * 12 * effectiveCTRate(data.estimatedCompanyProfit || 0);
      }
      return years;
    }

    return {
      labels: Array.from({ length: yearsToFreedom + 1 }, (_, i) => `${currentYear + i}`),
      flat: projectPension(false),
      stepped: projectPension(true),
      flatMonthly,
    };
  }, [data, yearsToFreedom, currentYear]);

  const pensionChartData = useMemo(() => ({
    labels: pensionScenarios.labels,
    datasets: [
      { label: `Flat £${pensionScenarios.flatMonthly}/mo`, data: pensionScenarios.flat, borderColor: '#94A3B8', backgroundColor: '#94A3B820', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 1 },
      { label: 'Stepped + CT relief', data: pensionScenarios.stepped, borderColor: '#001F8C', backgroundColor: '#001F8C20', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 1 },
    ],
  }), [pensionScenarios]);

  const lineOptions = (labelOverride?: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const, labels: { usePointStyle: true, pointStyle: 'circle' as const, padding: 12, font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${fmt(ctx.raw)}` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 9 }, maxTicksLimit: 10 } },
      y: { grid: { color: '#f3f4f6' }, ticks: { callback: (v: any) => fmtK(v), font: { size: 10 } }, beginAtZero: true },
    },
  });

  // ─── ISA vs Mortgage overpayment breakeven ────────────────────────────────
  const breakeven = useMemo(() => {
    const mortRate = (data.mortgageRate || 0) / 100;
    const isaReturn = G.isa;
    const extra = data.mortgageOP || 300; // monthly amount to compare

    let isaBal = 0;
    let mortgageSaved = 0;
    const rows: { year: number; isaValue: number; mortgageSaved: number }[] = [];

    for (let i = 0; i <= yearsToFreedom; i++) {
      rows.push({ year: currentYear + i, isaValue: Math.round(isaBal), mortgageSaved: Math.round(mortgageSaved) });
      isaBal = isaBal * (1 + isaReturn) + extra * 12;
      mortgageSaved += extra * 12 * mortRate; // interest saved (approximate)
    }

    const breakevenYear = rows.findIndex(r => r.isaValue > r.mortgageSaved && r.isaValue > 0);
    return { rows, breakevenYear: breakevenYear > 0 ? breakevenYear : null, monthlyAmount: extra };
  }, [data, yearsToFreedom, currentYear]);

  return (
    <div className="space-y-8">

      {/* ISA Scenarios */}
      <div>
        <h3 className="text-lg font-bold text-ma-navy mb-1">ISA Growth Scenarios</h3>
        <p className="text-xs text-gray-400 mb-4">Three contribution levels projected to freedom age ({data.targetFreedomAge || 60}). Growth at {(G.isa * 100).toFixed(1)}% pa.</p>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <SummaryCard label={`£${isaScenarios.low.monthly}/mo`} value={fmt(isaScenarios.low.projections[isaScenarios.low.projections.length - 1])} />
          <SummaryCard label={`£${isaScenarios.mid.monthly}/mo`} value={fmt(isaScenarios.mid.projections[isaScenarios.mid.projections.length - 1])} colour="text-ma-navy" />
          <SummaryCard label={`£${isaScenarios.high.monthly}/mo`} value={fmt(isaScenarios.high.projections[isaScenarios.high.projections.length - 1])} colour="text-green-600" />
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6" style={{ height: 320 }}>
          <Line data={isaChartData} options={lineOptions()} />
        </div>
      </div>

      {/* Pension flat vs stepped */}
      <div>
        <h3 className="text-lg font-bold text-ma-navy mb-1">Pension: Flat vs Stepped</h3>
        <p className="text-xs text-gray-400 mb-4">Flat contributions vs employer stepped schedule with CT relief reinvested.</p>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <SummaryCard label="Flat at retirement" value={fmt(pensionScenarios.flat[pensionScenarios.flat.length - 1])} />
          <SummaryCard label="Stepped + CT" value={fmt(pensionScenarios.stepped[pensionScenarios.stepped.length - 1])} colour="text-green-600" />
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6" style={{ height: 320 }}>
          <Line data={pensionChartData} options={lineOptions()} />
        </div>
      </div>

      {/* ISA vs Mortgage Overpayment */}
      <div>
        <h3 className="text-lg font-bold text-ma-navy mb-1">ISA vs Mortgage Overpayment</h3>
        <p className="text-xs text-gray-400 mb-4">
          If you put {fmt(breakeven.monthlyAmount)}/month into ISA instead of mortgage overpayments, when does the ISA value exceed the interest saved?
        </p>

        <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-3 py-2 text-left text-gray-400 font-medium">Year</th>
                <th className="px-3 py-2 text-right text-gray-400 font-medium">ISA value</th>
                <th className="px-3 py-2 text-right text-gray-400 font-medium">Mortgage interest saved</th>
                <th className="px-3 py-2 text-right text-gray-400 font-medium">Difference</th>
              </tr>
            </thead>
            <tbody>
              {breakeven.rows.slice(0, 15).map((r, i) => {
                const diff = r.isaValue - r.mortgageSaved;
                return (
                  <tr key={r.year} className={`border-b border-gray-50 ${i === breakeven.breakevenYear ? 'bg-green-50' : i % 2 ? 'bg-gray-50/50' : ''}`}>
                    <td className="px-3 py-1.5 text-gray-600">{r.year}</td>
                    <td className="px-3 py-1.5 text-right font-medium text-ma-navy">{fmt(r.isaValue)}</td>
                    <td className="px-3 py-1.5 text-right font-medium text-gray-600">{fmt(r.mortgageSaved)}</td>
                    <td className={`px-3 py-1.5 text-right font-medium ${diff >= 0 ? 'text-green-600' : 'text-red-500'}`}>{diff >= 0 ? '+' : ''}{fmt(diff)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {breakeven.breakevenYear !== null && (
          <p className="text-xs text-green-600 mt-2 font-medium">
            ISA overtakes mortgage overpayments in year {breakeven.breakevenYear} ({currentYear + breakeven.breakevenYear}).
          </p>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 4: FULL INTEGRATED PLAN
// ═══════════════════════════════════════════════════════════════════════════════

interface FullPlanYear {
  year: number;
  age: number;
  pension: number;
  isa: number;
  cash: number;
  mortgageBalance: number;
  netEquity: number;
  total: number;
  milestones: string[];
}

function projectFullPlan(data: ClientData): FullPlanYear[] {
  const G = getGrowth(data);
  const currentYear = new Date().getFullYear();
  const yearsToProject = Math.max(15, (data.targetFreedomAge || 60) - (data.clientAge || 40) + 5);

  let pension = data.pensionBalance || 0;
  let isa = data.investmentsBalance || 0;
  let cash = (data.efAccount1Balance || 0) + (data.efAccount2Balance || 0) + (data.otherSavingsBalance || 0);
  let mortBal = data.mortgageBalance || 0;

  const pensionMonthly = (data.pensionCompanyFunded || 0) + (data.pensionMonthly || 0);
  const isaMonthly = data.isaContribution || 0;
  const mortOP = data.mortgageOP || 0;
  const mortPayment = data.mortgagePayment || 0;
  const mortRate = (data.mortgageRate || 0) / 100;
  const efTarget = (data.efTargetMonths || 3) * ((data.livingCosts || 0) + (data.mortgagePayment || 0));

  const stepped = data.pensionSteppedSchedule || [];

  const years: FullPlanYear[] = [];

  for (let i = 0; i <= yearsToProject; i++) {
    const yr = currentYear + i;
    const age = (data.clientAge || 40) + i;
    const propGross = (data.propertyValue || 0) * Math.pow(1 + G.property, i) + (data.rentalPropertyValue || 0) * Math.pow(1 + G.property, i);
    const rentalMort = Math.max(0, (data.rentalMortgageBalance || 0) - (data.rentalMortgageType === 'repayment' ? 500 * 12 * i : 0));
    const netEquity = propGross - mortBal - rentalMort;

    // Milestones
    const milestones: string[] = [];
    if (i === 0) milestones.push('Today');
    if (cash >= efTarget && efTarget > 0 && i > 0) {
      // Check if it just crossed
      const prevCash = years[i - 1]?.cash || 0;
      if (prevCash < efTarget) milestones.push('EF Complete');
    }
    if (mortBal <= 0 && i > 0 && years[i - 1]?.mortgageBalance > 0) milestones.push('Mortgage-Free');
    if (age === (data.statePensionAge || 67)) milestones.push('State Pension');
    if (age === (data.targetFreedomAge || 60)) milestones.push('FREEDOM AGE');
    if (data.hasDbPension && age === (data.dbPensionStartAge || 67)) milestones.push('DB Pension Start');
    if (data.foreverHomeTargetYear && yr === data.foreverHomeTargetYear) milestones.push('Forever Home');
    // Pension steps
    const stepThisYear = stepped.find(s => s.fromYear === yr);
    if (stepThisYear) milestones.push(`Pension → £${stepThisYear.monthlyAmount}/mo`);

    years.push({
      year: yr, age,
      pension: Math.round(pension),
      isa: Math.round(isa),
      cash: Math.round(cash),
      mortgageBalance: Math.round(Math.max(0, mortBal)),
      netEquity: Math.round(netEquity),
      total: Math.round(pension + isa + cash + netEquity),
      milestones,
    });

    // Grow for next year
    // Pension — use stepped if available
    const activeStep = [...stepped].reverse().find(s => s.fromYear <= yr);
    const penMonth = activeStep ? activeStep.monthlyAmount + (data.pensionMonthly || 0) : pensionMonthly;
    pension = pension * (1 + G.pension) + penMonth * 12;
    isa = isa * (1 + G.isa) + isaMonthly * 12;
    cash = cash * (1 + G.cash);
    // Mortgage
    const interest = mortBal * mortRate;
    const principal = data.mortgageIsInterestOnly ? mortOP * 12 : Math.max(0, (mortPayment + mortOP) * 12 - interest);
    const annualLump = data.mortgageAnnualLumpSum || 0;
    mortBal = Math.max(0, mortBal - principal - annualLump);
  }

  return years;
}

function FullPlanTab({ data }: { data: ClientData }) {
  const plan = useMemo(() => projectFullPlan(data), [data]);
  const freedomYear = plan.find(p => p.age === (data.targetFreedomAge || 60));

  return (
    <>
      {/* Summary at freedom age */}
      {freedomYear && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <SummaryCard label={`Pension (Age ${freedomYear.age})`} value={fmt(freedomYear.pension)} />
          <SummaryCard label="ISA" value={fmt(freedomYear.isa)} />
          <SummaryCard label="Cash" value={fmt(freedomYear.cash)} />
          <SummaryCard label="Net equity" value={fmt(freedomYear.netEquity)} />
          <SummaryCard label="TOTAL" value={fmt(freedomYear.total)} colour="text-green-600" />
        </div>
      )}

      {/* Year-by-year table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-2 py-2 text-left text-gray-400 font-medium">Year</th>
              <th className="px-2 py-2 text-left text-gray-400 font-medium">Age</th>
              <th className="px-2 py-2 text-right text-gray-400 font-medium">Pension</th>
              <th className="px-2 py-2 text-right text-gray-400 font-medium">ISA</th>
              <th className="px-2 py-2 text-right text-gray-400 font-medium">Cash</th>
              <th className="px-2 py-2 text-right text-gray-400 font-medium">Mortgage</th>
              <th className="px-2 py-2 text-right text-gray-400 font-medium">Net equity</th>
              <th className="px-2 py-2 text-right text-gray-400 font-medium">Total</th>
              <th className="px-2 py-2 text-left text-gray-400 font-medium">Milestones</th>
            </tr>
          </thead>
          <tbody>
            {plan.map((y, i) => {
              const isFreedom = y.age === (data.targetFreedomAge || 60);
              return (
                <tr key={y.year} className={`border-b border-gray-50 ${isFreedom ? 'bg-green-50 font-semibold' : i % 2 ? 'bg-gray-50/50' : ''}`}>
                  <td className="px-2 py-1.5 text-gray-600">{y.year}</td>
                  <td className="px-2 py-1.5 text-gray-500">{y.age}</td>
                  <td className="px-2 py-1.5 text-right text-ma-navy">{fmtK(y.pension)}</td>
                  <td className="px-2 py-1.5 text-right" style={{ color: '#64DAD2' }}>{fmtK(y.isa)}</td>
                  <td className="px-2 py-1.5 text-right text-blue-400">{fmtK(y.cash)}</td>
                  <td className="px-2 py-1.5 text-right text-gray-500">{y.mortgageBalance > 0 ? fmtK(y.mortgageBalance) : <span className="text-green-600">Paid</span>}</td>
                  <td className="px-2 py-1.5 text-right text-emerald-600">{fmtK(y.netEquity)}</td>
                  <td className="px-2 py-1.5 text-right font-bold text-gray-800">{fmtK(y.total)}</td>
                  <td className="px-2 py-1.5">
                    {y.milestones.map(m => (
                      <span key={m} className={`inline-block text-xs px-1.5 py-0.5 rounded-full mr-1 ${
                        m === 'FREEDOM AGE' ? 'bg-green-200 text-green-800 font-bold' :
                        m === 'Mortgage-Free' ? 'bg-blue-100 text-blue-700' :
                        m.startsWith('Pension') ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{m}</span>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function SummaryCard({ label, value, colour }: { label: string; value: string; colour?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-bold ${colour || 'text-ma-navy'}`}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function WealthTrajectory({ data, onUpdate }: Props) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6">
        <h2 className="text-lg font-bold text-ma-navy mb-1">Wealth Trajectory — What-If Suite</h2>
        <p className="text-sm text-green-600">
          Explore different scenarios for your wealth pots, mortgage, and investments.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 pb-2">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-t-xl text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white border border-b-0 border-gray-200 text-ma-navy shadow-sm -mb-[3px]'
                : 'text-gray-500 hover:text-ma-navy hover:bg-gray-50'
            }`}
          >
            <span className="mr-1.5">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <OverviewTab data={data} />}
      {activeTab === 'mortgage' && <MortgageTab data={data} onUpdate={onUpdate} />}
      {activeTab === 'investment' && <InvestmentTab data={data} />}
      {activeTab === 'fullPlan' && <FullPlanTab data={data} />}

      {/* Assumptions */}
      {(() => {
        const G = getGrowth(data);
        return (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-xs text-gray-400 mt-6">
            <strong className="text-gray-500">Assumptions:</strong> Pension at {(G.pension * 100).toFixed(1)}%. ISA at {(G.isa * 100).toFixed(1)}%. Cash at {(G.cash * 100).toFixed(1)}%. Property at {(G.property * 100).toFixed(1)}%.
            Contributions continue at current plan levels. Mortgage reduces by scheduled payments + overpayments.
            This is an illustration only and not a guarantee of future performance.
          </div>
        );
      })()}
    </div>
  );
}
